<template>
  <div
    :key="`prompting-card-${cardId}`"
    class="flex flex-col justify-between flex-grow h-full gap-2"
  >
    <div class="flex items-center content-center justify-center h-full">
      <div
        :id="`prompting-scroll-${measurement.id}`"
        class="flex w-full gap-4 pb-4 overflow-x-auto scrolling-touch snap-x-mandatory scrollbar-lg"
        :class="{
          'max-w-56': sessionStatus === 'draft',
          'max-w-72': sessionStatus !== 'draft'
        }"
        dir="ltr"
        @scroll="onScroll"
      >
        <div
          v-for="(promptBoxes, idx) in promptBoxesPages"
          :key="`${measurement.id}-prompt-boxes-${idx + 1}`"
          :id="`${measurement.id}-prompt-boxes-${idx + 1}`"
          class="flex justify-center flex-shrink-0 w-full snap-center"
        >
          <div
            class="flex flex-wrap items-start content-center justify-center w-full gap-x-2 gap-y-3"
            :class="{ '-translate-y-1 scale-75': isCollapsed }"
          >
            <div
              v-for="prompt in promptBoxes"
              :key="prompt.key"
              class="space-y-1"
            >
              <div
                class="relative flex items-center justify-center flex-shrink-0 border cursor-pointer hover-darker"
                :class="{
                  'h-12 w-12 xl:h-14 xl:w-14 rounded-xl':
                    sessionStatus === 'draft',
                  'h-18 w-18 rounded-3xl': sessionStatus !== 'draft',
                  'cursor-wait':
                    (scoreLoadingBox !== null &&
                      scoreLoadingBox !== prompt.key) ||
                    (typeLoadingBox !== null && typeLoadingBox !== 1),
                  'pointer-events-none': sessionStatus !== 'ongoing'
                }"
                :style="{
                  backgroundColor: promptColors[prompt.color].primaryColor,
                  borderColor: promptColors[prompt.color].secondaryColor,
                  color: promptColors[prompt.color].textColor
                }"
                @click="onChangeScore(prompt, 1)"
              >
                <div
                  class="absolute font-semibold top-px"
                  :class="{
                    'text-xxs xl:text-xs': sessionStatus === 'draft',
                    'text-xs': sessionStatus !== 'draft'
                  }"
                >
                  {{ prompt.abbreviation }}
                </div>
                <div v-if="prompt.score" class="text-4xl font-bold">
                  {{ prompt.score }}
                </div>
                <div
                  v-else
                  class="font-bold"
                  :class="{
                    'text-3xl xl:text-4xl': sessionStatus === 'draft',
                    'text-5xl': sessionStatus !== 'draft'
                  }"
                >
                  +
                </div>
              </div>
              <div
                class="flex items-center justify-center h-5 transition-all duration-300 border rounded cursor-pointer border-slate-500 bg-pure-white hover:bg-hover-state"
                :class="{
                  'w-12 xl:w-14': sessionStatus === 'draft',
                  'w-18': sessionStatus !== 'draft',
                  'cursor-wait':
                    (scoreLoadingBox !== null &&
                      scoreLoadingBox !== prompt.key) ||
                    (typeLoadingBox !== null && typeLoadingBox !== -1),
                  'pointer-events-none':
                    !prompt.score || sessionStatus !== 'ongoing'
                }"
                @click="onChangeScore(prompt, -1)"
              >
                <div
                  class="flex-shrink-0 w-6 h-1 transition-all duration-300 rounded"
                  :class="{
                    'bg-slate-500': !prompt.score,
                    'bg-slate-600': prompt.score
                  }"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="flex-shrink-0 pb-3 space-y-2"
      :class="{ '-translate-y-2': isCollapsed }"
    >
      <div class="flex items-center justify-center h-3 gap-2">
        <div
          v-for="n in pageCount"
          :key="n"
          :class="{ 'bg-slate-700': n === page, 'bg-slate-400': n !== page }"
          class="w-3 h-3 transition-all duration-300 rounded-full cursor-pointer"
          @click="onChangePage(n)"
        ></div>
      </div>
      <div v-if="!isCollapsed">
        <div
          v-if="measurement.target.prompting_format === 'classic'"
          class="text-center"
        >
          <BlTextBody2 text="Goal:" />
          <BlTextBody2 :text="measurement.target.goal" />
          <BlTextBody2 text="attempt(s)" />
          <BlTextBody2 :text="measurement.target.success_metric" />
          <BlTextBody2 text="prompt" />
        </div>
        <div
          v-if="measurement.target.prompting_format === 'custom'"
          class="text-center"
        >
          <BlTextBody2 text="Goal:" />
          <BlTextBody2
            v-if="
              measurement.target.success_metric ===
                'equal to or greater than goal'
            "
            text="≥"
          />
          <BlTextBody2
            v-if="measurement.target.success_metric === 'less than goal'"
            text="<"
          />
          <BlTextBody2 :text="`${measurement.target.goal}%`" />
          <BlTextBody2 text="Score" class="pl-2" />
          <BlTextBody2 :text="`${currentScore}%`" />
        </div>
      </div>
      <BlInputButton
        v-if="sessionStatus === 'draft'"
        type="outlined"
        dense
        label="Customize prompt visibility"
        class="w-full"
        @clicked="customizePromptsOpened = true"
      />
    </div>

    <CustomizePromptModal
      v-if="customizePromptsOpened"
      :measurementId="measurement.id"
      :prompts="results"
      :successMetric="measurement.target.success_metric"
      @close-modal="customizePromptsOpened = false"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import debounce from "lodash/debounce";

import StatusChip from "../../clients/target/StatusChip.vue";
import CustomizePromptModal from "../modal/CustomizePromptModal.vue";

import BlTextTitle3 from "../../BlTextTitle3.vue";
import BlTextSubtitle2 from "../../BlTextSubtitle2.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlIcon from "../../BlIcon.vue";
import BlGif from "../../BlGif.vue";
import BlInputButton from "../../BlInputButton.vue";

export default {
  name: "MeasurementCardPrompting",
  components: {
    CustomizePromptModal,
    StatusChip,
    //
    BlTextTitle3,
    BlTextSubtitle2,
    BlTextSubtitle3,
    BlTextBody2,
    BlIcon,
    BlGif,
    BlInputButton
  },
  props: {
    measurement: { type: Object, required: true },
    measurementResults: { type: Object, required: true },
    target: { type: Object, required: true },
    sessionStatus: { type: String, required: true },
    isCollapsed: { type: Boolean, default: false }
    // @toggle-updated
  },
  data() {
    return {
      page: 1,
      results: {},
      scoreLoadingBox: null,
      typeLoadingBox: null,
      promptColors: {
        cherry: {
          primaryColor: "#FF4447",
          secondaryColor: "#FF4447",
          textColor: "#FFFFFF"
        },
        blush: {
          primaryColor: "#FFC1C2",
          secondaryColor: "#FF4447",
          textColor: "#FF4447"
        },
        gold: {
          primaryColor: "#F4D430",
          secondaryColor: "#F4D430",
          textColor: "#FFFFFF"
        },
        daffodil: {
          primaryColor: "#FBEEAC",
          secondaryColor: "#F4D430",
          textColor: "#9B7600"
        },
        lake: {
          primaryColor: "#2AB1A8",
          secondaryColor: "#2AB1A8",
          textColor: "#FFFFFF"
        },
        mint: {
          primaryColor: "#B8E5E2",
          secondaryColor: "#2AB1A8",
          textColor: "#2AB1A8"
        },
        sky: {
          primaryColor: "#487ADF",
          secondaryColor: "#487ADF",
          textColor: "#FFFFFF"
        },
        hydrangeas: {
          primaryColor: "#D3DFF7",
          secondaryColor: "#487ADF",
          textColor: "#487ADF"
        },
        grey: {
          primaryColor: "#E4E4E4",
          secondaryColor: "#A0A0A0",
          textColor: "#A0A0A0"
        },
        primary: {
          primaryColor: "#9A8AC2",
          secondaryColor: "#9A8AC2",
          textColor: "#FFFFFF"
        }
      },
      //
      customizePromptsOpened: false,
      cardId: "random-id"
    };
  },
  computed: {
    ...mapGetters(["viewMode"]),
    perPage() {
      return this.isCollapsed ? 3 : 9;
    },
    pageCount() {
      const results = Object.keys(this.results) || [];
      const boxes = results.filter(i => this.results[i].enabled).length;
      return Math.ceil(boxes / this.perPage);
    },
    promptBoxesPages() {
      if (!this.results) return [];
      const keys = Object.keys(this.results);
      if (!keys || !keys.length) return [];
      const prompts = keys
        .map(key => ({ ...this.results[key], key }))
        .filter(i => i.enabled)
        .sort((a, b) => a.position - b.position);
      const res = [];
      for (let idx = 1; idx <= this.pageCount; idx++) {
        const start = (idx - 1) * this.perPage;
        const end = idx * this.perPage;
        const arr = [...prompts.slice(start, end)];
        res.push(arr);
      }
      return res;
    },
    currentScore() {
      if (this.sessionStatus === "draft") return 0;
      if (!this.results) return 0;
      const keys = Object.keys(this.results);
      if (!keys || !keys.length) return 0;
      let count = 0;
      let total = 0;

      for (let idx = 0; idx < keys.length; idx++) {
        const key = keys[idx];
        if (this.results[key].enabled) {
          const promptScore = this.getPromptScore(key);
          const attempt = Number(this.results[key].score || 0);

          count += attempt;
          total += attempt * promptScore;
        }
      }
      const final = total / (count || 0);
      return Math.round(final || 0);
    }
  },
  watch: {
    scoreLoadingBox(val) {
      if (val === null) {
        this.$emit("toggle-updated", true);
      } else {
        this.$emit("toggle-updated", false);
      }
    },
    measurementResults: {
      deep: true,
      handler(val) {
        this.results = { ...val };
      }
    },
    isCollapsed() {
      setTimeout(() => {
        const id = `${this.measurement.id}-prompt-boxes-${1}`;
        const boxes = document.getElementById(id);
        if (boxes) {
          boxes.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "center"
          });
        }
      }, 300);
    },
    customizePromptsOpened() {
      const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
      let combination = "";
      for (let i = 0; i < 6; i++) {
        combination += chars[Math.round(Math.random() * 35)];
      }
      this.cardId = `card-random-id-${combination}`;
      this.results = { ...this.measurementResults };
    }
  },
  mounted() {
    this.results = { ...this.measurementResults };
  },
  methods: {
    ...mapActions(["updateMeasurementResults"]),
    onScroll(e) {
      const offsetWidth = e.currentTarget.offsetWidth;
      const scrollLeft = e.currentTarget.scrollLeft;
      const current = Math.floor(scrollLeft / offsetWidth) + 1;
      this.page = current;
    },
    getPromptScore(id) {
      const found = this.target.prompts.find(i => i.id === Number(id));
      return found ? Number(found.score || 0) : 0;
    },
    onChangePage(num) {
      const id = `${this.measurement.id}-prompt-boxes-${num}`;
      const boxes = document.getElementById(`${id}`);
      if (boxes) {
        boxes.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "center"
        });
      }
    },
    // ongoing
    onSaveScore: debounce(async function(prompt, score) {
      const params = {
        id: this.measurement.id,
        results: {}
      };
      params.results[prompt.key] = score;

      const { success, data } = await this.updateMeasurementResults(params);
      this.scoreLoadingBox = null;
      this.typeLoadingBox = null;
      if (!success) {
        this.results = { ...this.measurementResults };
        return;
      }

      this.results = { ...data.results };
    }, 1000),
    async onChangeScore(prompt, score) {
      if (this.sessionStatus !== "ongoing") return;
      if (
        this.scoreLoadingBox !== null &&
        this.scoreLoadingBox !== prompt.key
      ) {
        return;
      }
      if (this.typeLoadingBox !== null && this.typeLoadingBox !== score) {
        return;
      }

      // change state
      const newScore = this.results[prompt.key].score + score;
      this.results[prompt.key] = {
        ...this.results[prompt.key],
        score: newScore
      };

      // save state
      const gapScore = newScore - this.measurementResults[prompt.key].score;
      this.scoreLoadingBox = prompt.key;
      this.typeLoadingBox = score;
      this.onSaveScore(prompt, gapScore);
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/sessions/card/Prompting.vue